import { useEffect, useState } from 'react'

interface ErrorWithMessage {
  message: string
}

type UnrepliedCommentsState = {
  count: number
  loading: boolean
  error: ErrorWithMessage | null
  showBadge: boolean
}

type UnrepliedCommentsEvent = CustomEvent<{
  count: number
  loading: boolean
  error?: ErrorWithMessage
}>

/**
 * Hook that listens to unreplied comments count events from publish-frontend
 * and determines if we should show a badge with the number of unreplied comments
 */
export const useShouldShowUnrepliedCommentsBadge =
  (): UnrepliedCommentsState => {
    const [state, setState] = useState<UnrepliedCommentsState>({
      count: 0,
      loading: false,
      error: null,
      showBadge: false,
    })

    useEffect(() => {
      const { eventKeys } = window.appshell || {}
      if (!eventKeys?.UNREPLIED_COMMENTS_COUNT_EVENT_KEY) {
        return
      }

      function onUnrepliedCommentsUpdate(event: UnrepliedCommentsEvent): void {
        const { count, loading, error } = event.detail || {
          count: 0,
          loading: false,
        }
        setState((prev) => {
          const newCount = typeof count === 'number' ? count : prev.count
          const newLoading = loading ?? false
          const newError = error ?? null

          // Keep showing badge if previously true and we're just loading
          const showBadge = newCount > 0 && (!newLoading || prev.showBadge)

          return {
            count: newCount,
            loading: newLoading,
            error: newError,
            showBadge,
          }
        })
      }

      window.addEventListener(
        eventKeys.UNREPLIED_COMMENTS_COUNT_EVENT_KEY,
        onUnrepliedCommentsUpdate as EventListener,
      )

      return () => {
        window.removeEventListener(
          eventKeys.UNREPLIED_COMMENTS_COUNT_EVENT_KEY,
          onUnrepliedCommentsUpdate as EventListener,
        )
      }
    }, [])

    return state
  }
